/**
 * PLW: the per-product mute/unmute toggle on PDP gallery videos.
 *
 * That is ALL this module does (2026-08-31). Gallery videos are plain native
 * `<video autoplay muted loop playsinline>` elements (product-media.liquid) —
 * the browser owns autoplay; there is no IntersectionObserver, no play()
 * retry and no resume logic here any more (client reports of mobile videos
 * not autoplaying through the previous JS-driven playback).
 *
 * The toggle ([data-plw-video-sound], rendered when custom.video_sound opts
 * the product in — client QA 2026-08-18) is wired by delegation so gallery
 * re-renders on variant change need no re-binding. One sound source at a
 * time: unmuting a video mutes every other gallery video.
 */

const SOUND_BUTTON_SELECTOR = '[data-plw-video-sound]';

/**
 * Reflects a video's muted state onto its sibling sound toggle (icon swap via
 * data-muted, action-phrased aria-label from the data-label-* attributes).
 * @param {HTMLVideoElement} video
 */
function syncSoundButton(video) {
  const button = video.closest('.product-media')?.querySelector(SOUND_BUTTON_SELECTOR);
  if (!button) return;
  button.setAttribute('data-muted', String(video.muted));
  const label = video.muted ? button.getAttribute('data-label-unmute') : button.getAttribute('data-label-mute');
  if (label) button.setAttribute('aria-label', label);
}

// The gallery's zoom trigger is an ANCESTOR slide: its handler runs via
// Horizon's document-capture on:click pipeline, which a later listener can't
// stop — the button's own dummy on:click attribute claims that pipeline's
// click instead (see product-media.liquid). The stops below only guard
// against ordinary bubble listeners.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest(SOUND_BUTTON_SELECTOR) : null;
    if (!button) return;

    const video = button.closest('.product-media')?.querySelector('video');
    if (!video) return;

    event.preventDefault();
    event.stopPropagation();

    const unmuting = video.muted;
    if (unmuting) {
      // One sound source at a time — silence every other gallery video.
      for (const other of document.querySelectorAll('.product-media video')) {
        if (other === video || other.muted) continue;
        other.muted = true;
        syncSoundButton(other);
      }
    }
    video.muted = !unmuting;
    syncSoundButton(video);
    // The click is a user gesture, so an unmuted (re)play is always allowed.
    if (unmuting && video.paused) video.play().catch(() => {});
  },
  true
);
